import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Checkbox, Popover, Space, Spin, message } from "antd";
import { Link } from "react-router-dom";
import moment from "moment";
import styled from "styled-components";
import { MAIN_BAND_ID, WITHOUT_DEVICE_MULTIPLE_ACTION_BANDS } from "../../../constants";
import { ScootStatusToKorean, BelongingIdToKorean } from "../../../utils/Translate";
import ScootInspectModal from "../../../components/ScootInspectModal";
import { Address, getAddress, getAddressBulk } from "../../../utils/address.utils";
import { getBands } from "../../bands/bands.service";
import { getCapitals } from "../../belongings/belongings.service";
import { getAreasWithLocationByType } from "../../areas/areas.service";
import { AreaDto } from "../../areas/area.interface";
import { useAuthContext } from "../../auth/context/useAuthContext";
import { useResourceContext } from "../../common/resource/useResourceContext";
import { TableTitleTextWrapper, TableTitleWrapper, VehicleTable } from "../../common/table/table.style";
import { Wrapper } from "../../used_scoots/components/UsedScootStyled";
import { AddTaskDefinitionTypeModal } from "../component/AddTaskDefinitionTypeModal";
import { VehicleListV2Actions } from "../component/VehicleListV2Actions";
import { VehicleListV2Filter } from "../component/VehicleListV2Filter";
import { getTaskDefinitions, getVehicleCountsV2, getVehicleLastRidesV2, getVehiclesV2, resolveVehicleSelectionIdsV2 } from "../service/scooters.service";
import {
  ScooterStatusType,
  TaskDefinitionDTO,
  VehicleListV2Counts,
  VehicleListV2FilterValue,
  VehicleListV2Item,
  VehicleListV2Response,
  VehicleListV2Selection,
  VehicleListV2Sort,
} from "../service/scooter.interface";
import {
  compactVehicleListV2Query,
  shouldResetVehicleListFilters,
  vehicleListV2SortDirection,
} from "../service/vehicle-list-v2.utils";

const RESTRICTED_FILTER_BAND_ID = "clypl7wgb03zf0l78xijzhen5";
const ADDRESS_FALLBACK_CONCURRENCY = 5;
// A show-all action falls back for at most one default page; other rows remain individually retryable.
const ADDRESS_FALLBACK_LIMIT = 10;
const EMPTY_RESPONSE: VehicleListV2Response = { items: [], page: 0, size: 10, totalElements: 0, totalPages: 0 };

function hasAddress(address?: Address | null): address is Address {
  return !!(address && (address.fullAddress || address.address));
}

function hasLocation(item: VehicleListV2Item): item is VehicleListV2Item & { lat: number; lng: number } {
  return Boolean(item.lat) && item.lng != null;
}

function initialFilters(isMainBand: boolean): VehicleListV2FilterValue {
  return {
    bandIds: isMainBand ? [MAIN_BAND_ID] : undefined,
    allBands: false,
    vehicleTypes: ["SCOOTER", "BIKE"],
    statuses: isMainBand ? ["Ready"] : undefined,
  };
}

export function VehicleListV2Page() {
  const { auth, hasPermission } = useAuthContext();
  const { R } = useResourceContext();
  const bandId = auth?.band?.id;
  const isMainBand = auth?.band?.id === MAIN_BAND_ID;
  const showAdminFilters = auth?.band?.id !== RESTRICTED_FILTER_BAND_ID;
  const [response, setResponse] = useState<VehicleListV2Response>(EMPTY_RESPONSE);
  const [responseQueryKey, setResponseQueryKey] = useState("");
  const [globalCounts, setGlobalCounts] = useState<VehicleListV2Counts | null>(null);
  const [countsLoading, setCountsLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [filters, setFilters] = useState<VehicleListV2FilterValue>(initialFilters(isMainBand));
  const [qr, setQr] = useState("");
  const [imei, setImei] = useState("");
  const [debouncedIdentifiers, setDebouncedIdentifiers] = useState({ qr: "", imei: "" });
  const debouncedIdentifiersRef = useRef({ qr: "", imei: "" });
  const [sort, setSort] = useState<VehicleListV2Sort>("ID");
  const [direction, setDirection] = useState<"ASC" | "DESC">("ASC");
  const [bands, setBands] = useState<any[]>([]);
  const [belongings, setBelongings] = useState<any[]>([]);
  const [areas, setAreas] = useState<AreaDto[]>([]);
  const [areasLoading, setAreasLoading] = useState(false);
  const [areasLoaded, setAreasLoaded] = useState(false);
  const [taskDefinitions, setTaskDefinitions] = useState<TaskDefinitionDTO[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;
  const [selectionSnapshots, setSelectionSnapshots] = useState<Record<string, string[]>>({});
  const [selectingAll, setSelectingAll] = useState(false);
  const selectionScope = useRef(0);
  const [inspectVisible, setInspectVisible] = useState(false);
  const [inspectMounted, setInspectMounted] = useState(false);
  const [addTaskVisible, setAddTaskVisible] = useState(false);
  const [reloadSequence, setReloadSequence] = useState(0);
  const [lastRideAtByVehicleId, setLastRideAtByVehicleId] = useState<Record<string, string | null>>({});
  const [lastRideLoaded, setLastRideLoaded] = useState(false);
  const [lastRideRevision, setLastRideRevision] = useState(0);
  const [lastRideWaitingForPage, setLastRideWaitingForPage] = useState(false);
  const selectedItems = useRef<Record<string, VehicleListV2Item>>({});
  const [lastRideLoadingKey, setLastRideLoadingKey] = useState<string | null>(null);
  const [visibleAddressIds, setVisibleAddressIds] = useState<string[]>([]);
  const [addressResults, setAddressResults] = useState<Record<string, Address | null>>({});
  const [addressLoadingIds, setAddressLoadingIds] = useState<string[]>([]);
  const [addressRetryIds, setAddressRetryIds] = useState<string[]>([]);
  const [addressesLoadingAll, setAddressesLoadingAll] = useState(false);
  const requestSequence = useRef(0);
  const pageCache = useRef<Record<string, VehicleListV2Response>>({});
  const previousBandId = useRef(auth?.band?.id);
  const countRequestSequence = useRef(0);
  const areaRequestSequence = useRef(0);
  const areaRequestInFlight = useRef(false);
  const addressRequestSequence = useRef(0);
  const lastRideScopeSequence = useRef(0);
  const lastRideRequestsInFlight = useRef<Set<string>>(new Set());

  const updateDebouncedIdentifiers = useCallback((nextQr: string, nextImei: string) => {
    if (debouncedIdentifiersRef.current.qr === nextQr && debouncedIdentifiersRef.current.imei === nextImei) return;
    debouncedIdentifiersRef.current = { qr: nextQr, imei: nextImei };
    setPage(0);
    setDebouncedIdentifiers({ qr: nextQr, imei: nextImei });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds([]);
    setSelectionSnapshots({});
    selectedItems.current = {};
    selectionScope.current += 1;
    setSelectingAll(false);
  }, []);
  const refresh = useCallback(() => {
    clearSelection();
    setLastRideLoaded(false);
    setReloadSequence((value) => value + 1);
  }, [clearSelection]);

  useEffect(() => {
    const timeout = window.setTimeout(() => updateDebouncedIdentifiers(qr, imei), 300);
    return () => window.clearTimeout(timeout);
  }, [qr, imei, updateDebouncedIdentifiers]);

  useEffect(() => {
    if (!bandId) return;
    const sequence = ++countRequestSequence.current;
    setCountsLoading(true);
    getVehicleCountsV2()
      .then((counts) => {
        if (sequence === countRequestSequence.current) setGlobalCounts(counts);
      })
      .catch(() => {
        if (sequence !== countRequestSequence.current) return;
        setGlobalCounts(null);
        message.error("전체 기기 현황을 불러오지 못했습니다. 새로고침으로 다시 시도해주세요.");
      })
      .finally(() => {
        if (sequence === countRequestSequence.current) setCountsLoading(false);
      });
    return () => { countRequestSequence.current += 1; };
  }, [bandId, reloadSequence]);

  useEffect(() => {
    Promise.all([
      getBands().then(setBands),
      getCapitals().then(setBelongings),
      getTaskDefinitions(false).then((items) => setTaskDefinitions(items || [])),
    ]).catch(() => message.error("필터 정보를 불러오지 못했습니다."));
  }, [reloadSequence]);

  useEffect(() => {
    areaRequestSequence.current += 1;
    areaRequestInFlight.current = false;
    setAreas([]);
    setAreasLoaded(false);
    setAreasLoading(false);
  }, [bandId, reloadSequence]);

  const loadAreas = useCallback(() => {
    if (!bandId || areasLoaded || areaRequestInFlight.current) return;
    const sequence = ++areaRequestSequence.current;
    areaRequestInFlight.current = true;
    setAreasLoading(true);
    getAreasWithLocationByType("Normal, Collecting" as any, false)
      .then((items) => {
        if (sequence === areaRequestSequence.current) {
          setAreas(items);
          setAreasLoaded(true);
        }
      })
      .catch(() => {
        if (sequence === areaRequestSequence.current) message.error("GPS 지역 목록을 불러오지 못했습니다. 다시 열어주세요.");
      })
      .finally(() => {
        if (sequence === areaRequestSequence.current) {
          areaRequestInFlight.current = false;
          setAreasLoading(false);
        }
      });
  }, [bandId, areasLoaded]);

  useEffect(() => {
    if (!auth) return;
    if (!shouldResetVehicleListFilters(previousBandId.current, auth.band.id)) return;
    previousBandId.current = auth.band.id;
    setFilters(initialFilters(isMainBand));
    setQr("");
    setImei("");
    updateDebouncedIdentifiers("", "");
    setLastRideLoaded(false);
    setPage(0);
    clearSelection();
  }, [auth, bandId, isMainBand, clearSelection, updateDebouncedIdentifiers]);

  const filterQuery = useMemo(() => compactVehicleListV2Query(filters, debouncedIdentifiers), [filters, debouncedIdentifiers]);
  const query = useMemo(() => filterQuery.lastRideDays !== undefined || filterQuery.lastRideDaysRaw !== undefined || sort === "LAST_RIDE_AT"
    ? { ...filterQuery, lastRideLoaded }
    : filterQuery, [filterQuery, sort, lastRideLoaded]);
  const selectionQueryKey = JSON.stringify({ bandId, query });
  const queryKey = JSON.stringify({ bandId: auth?.band?.id, page, size, query, sort, direction, reloadSequence,
    lastRideRevision: query.lastRideLoaded !== undefined ? lastRideRevision : undefined,
  });

  useEffect(() => {
    selectionScope.current += 1;
    setSelectingAll(false);
  }, [selectionQueryKey]);

  useEffect(() => {
    lastRideScopeSequence.current += 1;
    lastRideRequestsInFlight.current.clear();
    setLastRideAtByVehicleId({});
    setLastRideLoaded(false);
    setLastRideWaitingForPage(false);
    setLastRideLoadingKey(null);
    pageCache.current = {};
  }, [bandId, reloadSequence]);

  useEffect(() => {
    addressRequestSequence.current += 1;
    setVisibleAddressIds([]);
    setAddressResults({});
    setAddressLoadingIds([]);
    setAddressRetryIds([]);
    setAddressesLoadingAll(false);
  }, [queryKey]);

  useEffect(() => {
    if (!bandId) return;
    const sequence = ++requestSequence.current;
    setLoading(true);
    setError("");
    const cached = pageCache.current[queryKey];
    if (cached) {
      delete pageCache.current[queryKey];
      pageCache.current[queryKey] = cached;
      setResponse(cached);
      setResponseQueryKey(queryKey);
      setLoading(false);
      return;
    }
    getVehiclesV2({ page, size, ...query, sort, direction, includeCounts: false }, false)
      .then((result) => {
        if (sequence !== requestSequence.current) return;
        const snapshot = { ...result, items: result.items.map((item) => ({
          ...item,
          allocatedDays: item.allocatedAt ? moment().utc().diff(moment(item.allocatedAt), "days") : "",
        })) };
        pageCache.current[queryKey] = snapshot;
        setResponse(snapshot);
        setResponseQueryKey(queryKey);
      })
      .catch((requestError) => {
        if (sequence === requestSequence.current) {
          setResponse(EMPTY_RESPONSE);
          setResponseQueryKey("");
          setError(requestError?.response?.data?.message || "기기 목록을 불러오지 못했습니다.");
        }
      })
      .finally(() => sequence === requestSequence.current && setLoading(false));
  }, [bandId, page, size, query, sort, direction, reloadSequence, queryKey]);

  const bandNames = useMemo(() => new Map<string, string>(bands.map((band) => [band.id, band.name])), [bands]);
  const belongingNames = useMemo(() => new Map<number, string>(belongings.map((item) => [item.id, item.description])), [belongings]);
  const currentPageIds = useMemo(() => response.items.map((item) => item.id), [response.items]);
  const lastRidePageKey = JSON.stringify({ bandId, vehicleIds: currentPageIds });
  const addressableIds = response.items.filter(hasLocation).map((item) => item.id);
  const allAddressesVisible = addressableIds.length > 0 && addressableIds.every((id) => visibleAddressIds.includes(id) && hasAddress(addressResults[id]));
  const selectedRowKeys = selectedIds;
  const selectedCount = selectedIds.length;
  const selection: VehicleListV2Selection | null = selectedCount ? { mode: "IDS", vehicleIds: selectedIds } : null;
  const selectedIdSet = new Set(selectedIds);
  const currentPageSelectedCount = currentPageIds.filter((id) => selectedIdSet.has(id)).length;
  const currentSnapshot = selectionSnapshots[selectionQueryKey];
  const querySelected = !!currentSnapshot?.length && currentSnapshot.every((id) => selectedIdSet.has(id));
  const selectionReady = !loading && !error && responseQueryKey === queryKey
    && qr === debouncedIdentifiers.qr && imei === debouncedIdentifiers.imei;
  const currentPageSelected = response.items.length > 0 && currentPageIds.every((id) => selectedRowKeys.includes(id));
  const currentPagePartiallySelected = currentPageIds.some((id) => selectedRowKeys.includes(id)) && !currentPageSelected;

  const loadLastRides = useCallback(async (reloadQuery = true) => {
    if (!selectionReady || lastRideRequestsInFlight.current.has(lastRidePageKey)) return;
    if (!currentPageIds.length) {
      if (!lastRideLoaded && (query.lastRideDays !== undefined || query.lastRideDaysRaw !== undefined)) {
        setLastRideWaitingForPage(true);
        setLastRideLoaded(true);
      }
      return;
    }
    const key = lastRidePageKey;
    const scope = lastRideScopeSequence.current;
    lastRideRequestsInFlight.current.add(key);
    setLastRideLoadingKey(key);
    try {
      const result = await getVehicleLastRidesV2(currentPageIds);
      if (scope === lastRideScopeSequence.current) {
        setLastRideAtByVehicleId((previous) => ({ ...previous, ...result.lastRideAtByVehicleId }));
        setLastRideLoaded(true);
        if (reloadQuery && lastRideLoaded && query.lastRideLoaded !== undefined) {
          setLastRideRevision((previous) => previous + 1);
        }
      }
    } catch {
      if (scope === lastRideScopeSequence.current) message.error("마지막 라이드 날짜를 불러오지 못했습니다.");
    } finally {
      if (scope === lastRideScopeSequence.current) {
        lastRideRequestsInFlight.current.delete(key);
        setLastRideLoadingKey((previous) => previous === key ? null : previous);
      }
    }
  }, [selectionReady, currentPageIds, lastRidePageKey, lastRideLoaded, query]);

  useEffect(() => {
    if (!lastRideWaitingForPage || (!selectionReady && !error)) return;
    setLastRideWaitingForPage(false);
    if (selectionReady && currentPageIds.length) void loadLastRides(false);
  }, [lastRideWaitingForPage, selectionReady, error, currentPageIds, loadLastRides]);

  const loadAddress = async (row: VehicleListV2Item) => {
    if (!selectionReady || !hasLocation(row) || addressLoadingIds.includes(row.id)) return;
    const sequence = addressRequestSequence.current;
    setVisibleAddressIds((previous) => previous.includes(row.id) ? previous : [...previous, row.id]);
    if (hasAddress(addressResults[row.id])) return;
    setAddressRetryIds((previous) => previous.filter((id) => id !== row.id));
    setAddressLoadingIds((previous) => [...previous, row.id]);
    const address = await getAddress({ latitude: row.lat, longitude: row.lng });
    if (sequence !== addressRequestSequence.current) return;
    setAddressResults((previous) => ({ ...previous, [row.id]: address }));
    if (!hasAddress(address)) setAddressRetryIds((previous) => [...previous, row.id]);
    setAddressLoadingIds((previous) => previous.filter((id) => id !== row.id));
  };

  const loadAllAddresses = async () => {
    if (!selectionReady || addressesLoadingAll) return;
    const sequence = addressRequestSequence.current;
    const previouslyVisibleIds = visibleAddressIds;
    const rows = response.items.filter(hasLocation);
    const pendingRows = rows.filter((item) => !hasAddress(addressResults[item.id]) && !addressLoadingIds.includes(item.id) && !addressRetryIds.includes(item.id));
    setVisibleAddressIds(rows.map((item) => item.id));
    if (!pendingRows.length) return;
    setAddressesLoadingAll(true);
    setAddressLoadingIds((previous) => Array.from(new Set([...previous, ...pendingRows.map((item) => item.id)])));
    let bulkResults: (Address | null)[];
    try {
      bulkResults = await getAddressBulk(pendingRows.map((item) => ({ latitude: item.lat, longitude: item.lng })), true);
    } catch {
      if (sequence === addressRequestSequence.current) {
        setVisibleAddressIds(previouslyVisibleIds);
        setAddressLoadingIds((previous) => previous.filter((id) => !pendingRows.some((item) => item.id === id)));
        setAddressesLoadingAll(false);
        message.error("주소를 불러오지 못했습니다. 다시 시도해주세요.");
      }
      return;
    }
    if (sequence !== addressRequestSequence.current) return;

    const resolved: Record<string, Address> = {};
    const unresolved: Array<VehicleListV2Item & { lat: number; lng: number }> = [];
    pendingRows.forEach((item, index) => {
      const address = bulkResults[index];
      if (hasAddress(address)) resolved[item.id] = address;
      else unresolved.push(item);
    });
    setAddressResults((previous) => ({ ...previous, ...resolved }));
    setAddressLoadingIds((previous) => previous.filter((id) => !resolved[id]));

    const fallbackRows = unresolved.slice(0, ADDRESS_FALLBACK_LIMIT);
    const retryIds = unresolved.slice(ADDRESS_FALLBACK_LIMIT).map((item) => item.id);
    setAddressRetryIds((previous) => Array.from(new Set([...previous, ...retryIds])));
    setAddressLoadingIds((previous) => previous.filter((id) => !retryIds.includes(id)));
    let nextIndex = 0;
    await Promise.all(Array.from({ length: Math.min(ADDRESS_FALLBACK_CONCURRENCY, fallbackRows.length) }, async () => {
      while (nextIndex < fallbackRows.length && sequence === addressRequestSequence.current) {
        const item = fallbackRows[nextIndex++];
        const address = await getAddress({ latitude: item.lat, longitude: item.lng });
        if (sequence !== addressRequestSequence.current) return;
        setAddressResults((previous) => ({ ...previous, [item.id]: address }));
        if (!hasAddress(address)) setAddressRetryIds((previous) => [...previous, item.id]);
        setAddressLoadingIds((previous) => previous.filter((id) => id !== item.id));
      }
    }));
    if (sequence === addressRequestSequence.current) setAddressesLoadingAll(false);
  };

  const changeFilters = (next: VehicleListV2FilterValue) => {
    setFilters(next);
    setPage(0);
  };

  const setCurrentPageSelected = (selected: boolean) => {
    if (!selectionReady) return;
    clearSelection();
    if (selected) {
      selectedItems.current = Object.fromEntries(response.items.map((item) => [item.id, item]));
      setSelectedIds(currentPageIds);
    }
  };

  const selectSearchResults = async () => {
    if (!selectionReady || selectingAll) return;
    const scope = selectionScope.current;
    const key = selectionQueryKey;
    setSelectingAll(true);
    try {
      const result = await resolveVehicleSelectionIdsV2({ selection: { mode: "QUERY", query } });
      if (scope !== selectionScope.current) return;
      const snapshots: Record<string, VehicleListV2Item> = {};
      const visitedItems = new Map<string, VehicleListV2Item>();
      const visibleItems = new Map(response.items.map((item) => [item.id, item]));
      Object.values(pageCache.current).forEach((cached) => cached.items.forEach((item) => visitedItems.set(item.id, item)));
      result.vehicleIds.forEach((id) => {
        const item = selectedItems.current[id] || visibleItems.get(id) || visitedItems.get(id);
        if (item) snapshots[id] = item;
      });
      selectedItems.current = snapshots;
      setSelectedIds(result.vehicleIds);
      setSelectionSnapshots({ [key]: result.vehicleIds });
    } catch {
      if (scope === selectionScope.current) message.error("검색 결과를 선택하지 못했습니다. 다시 시도해주세요.");
    } finally {
      if (scope === selectionScope.current) setSelectingAll(false);
    }
  };

  const selectionPopover = querySelected ? (
    <Space direction="vertical" size={4}>
      <span>검색 결과 {currentSnapshot.length.toLocaleString()}대 전체 선택됨</span>
      <Button type="link" size="small" onClick={clearSelection}>선택 해제</Button>
    </Space>
  ) : (
    <Space direction="vertical" size={4}>
      <span>현재 페이지 {currentPageSelectedCount.toLocaleString()}대 선택됨</span>
      <Button type="link" size="small" loading={selectingAll} onClick={selectSearchResults}>
        검색 결과 {response.totalElements.toLocaleString()}대 전체 선택
      </Button>
    </Space>
  );

  const columns: any[] = [
    { title: R.text.qr_or_imei, dataIndex: "shortImei", render: (value: string, row: VehicleListV2Item) => <CustomLink target="_blank" to={`/app/scoot/${row.imei}`}>{value || R.text.empty}</CustomLink> },
    { title: R.text.device_model, dataIndex: "smodel" },
    { title: R.text.region_name, dataIndex: "bandId", render: (value: string) => bandNames.get(value) || value },
    { title: R.text.device_owner, dataIndex: "belongingId", render: (value: number) => belongingNames.get(value) || BelongingIdToKorean[value] || value },
    { title: R.text.battery, dataIndex: "battery", sorter: true, sortOrder: sort === "BATTERY" ? (direction === "ASC" ? "ascend" : "descend") : null, render: (value: number) => <div style={{ width: 80 }}>{value}%</div> },
    {
      title: <Space size={4}>{R.text.gps_location}<Button size="small" loading={addressesLoadingAll} disabled={!selectionReady || !addressableIds.length || (allAddressesVisible && !addressesLoadingAll)} onClick={loadAllAddresses}>{allAddressesVisible && !addressesLoadingAll ? "주소 모두 표시됨" : "주소 모두 표시"}</Button></Space>,
      dataIndex: "lat",
      render: (_: number, row: VehicleListV2Item) => hasLocation(row)
        ? visibleAddressIds.includes(row.id)
          ? addressLoadingIds.includes(row.id)
            ? <Spin size="small" />
            : hasAddress(addressResults[row.id])
              ? <CustomLink target="_blank" to={{ pathname: `/app/map/route/${row.imei}`, search: `?custom=${JSON.stringify([{ lat: row.lat, lng: row.lng, text: "Now" }])}` }}>{addressResults[row.id]?.fullAddress || addressResults[row.id]?.address}</CustomLink>
              : <Button size="small" disabled={!selectionReady} onClick={() => loadAddress(row)}>다시 시도</Button>
          : <Button size="small" disabled={!selectionReady} onClick={() => loadAddress(row)}>주소 표시</Button>
        : R.text.empty,
    },
    { title: R.text.device_status, dataIndex: "status", render: (value: ScooterStatusType) => ScootStatusToKorean[value] || value },
    { title: "작업상태", dataIndex: "workDefinition", render: (value: any) => value?.displayName || "-" },
    { title: R.text.faulty, dataIndex: "broken", render: (value: boolean) => value ? "O" : "X" },
    { title: "백업모드", dataIndex: "backupMode", render: (value: boolean) => value ? "O" : "X" },
    { title: "이용가능여부", dataIndex: "available", render: (value: boolean) => value ? "O" : "X" },
    { title: R.text.ride_status, dataIndex: "riding", render: (value: boolean) => value ? "O" : "X" },
    { title: R.text.lock_status, dataIndex: "locked", render: (value: boolean) => value ? R.text.locked : R.text.unlocked },
    { title: R.text.iot_status, dataIndex: "online", render: (value: boolean) => value ? R.text.normal : R.text.offline },
    { title: R.text.placement_date, dataIndex: "allocatedAt", sorter: true, sortOrder: sort === "ALLOCATED_AT" ? (direction === "DESC" ? "ascend" : "descend") : null, render: (value: string | undefined, row: VehicleListV2Item) => value ? `${row.allocatedDays}${R.text.days}` : R.text.undeployed },
    { title: R.text.inspection_status, dataIndex: "inspected", render: (value: boolean) => value ? "O" : "X" },
    { title: R.text.last_iot_received, dataIndex: "dataUpdatedAt", sorter: true, sortOrder: sort === "IOT_UPDATED_AT" ? (direction === "DESC" ? "ascend" : "descend") : null, render: (value?: string) => value ? moment.utc(value).local().format("YY-MM-DD HH:mm:ss") : "-" },
    { title: "마지막 작업일자", dataIndex: "lastWorkAt", sorter: true, sortOrder: sort === "LAST_WORK_AT" ? (direction === "DESC" ? "ascend" : "descend") : null, render: (value?: string) => value ? moment(value).format("YY-MM-DD HH:mm:ss") : "-" },
    { title: R.text.last_ride, dataIndex: "lastRideAt", sorter: true, sortOrder: sort === "LAST_RIDE_AT" ? (direction === "DESC" ? "ascend" : "descend") : null, render: (_: string, row: VehicleListV2Item) => {
      const value = selectionReady ? lastRideAtByVehicleId[row.id] : undefined;
      return value ? moment.utc(value).local().format("YY-MM-DD HH:mm:ss") : "-";
    } },
  ];
  if (isMainBand) columns.splice(-1, 0, { title: "일본기기", dataIndex: "fromJapan", render: (value: boolean) => value ? "O" : "X" });
  const multipleActionsDisabled = !!auth && WITHOUT_DEVICE_MULTIPLE_ACTION_BANDS.includes(auth.band.id);

  return (
    <>
      {error && <ErrorAlert type="error" showIcon message={error} action={<Button size="small" onClick={refresh}>다시 시도</Button>} />}
      <VehicleStyleTable
        loading={loading}
        rowKey="id"
        columns={columns}
        dataSource={response.items}
        scroll={{ x: "max-content" }}
        rowSelection={{
          selectedRowKeys,
          preserveSelectedRowKeys: true,
          getCheckboxProps: () => ({ disabled: !selectionReady }),
          columnTitle: <Popover visible={selectionReady && (currentPageSelected || currentPagePartiallySelected) && response.totalElements > 0} content={selectionPopover} placement="topLeft" overlayInnerStyle={{ border: "1px solid #91caff", borderRadius: 6, boxShadow: "0 6px 20px rgba(24, 144, 255, 0.18)" }}><Checkbox disabled={!selectionReady} checked={currentPageSelected} indeterminate={currentPagePartiallySelected} onChange={(event) => setCurrentPageSelected(event.target.checked)} /></Popover>,
          onSelect: (record: any, selected: boolean) => {
            if (!selectionReady) return;
            selectionScope.current += 1;
            setSelectingAll(false);
            if (selected) selectedItems.current[record.id] = record;
            else delete selectedItems.current[record.id];
            setSelectedIds((previous) => selected ? Array.from(new Set([...previous, record.id])) : previous.filter((id) => id !== record.id));
          },
          onSelectMultiple: (selected: boolean, _selectedRows: object[], changedRows: object[]) => {
            if (!selectionReady) return;
            selectionScope.current += 1;
            setSelectingAll(false);
            const records = changedRows as VehicleListV2Item[];
            records.forEach((record) => {
              if (selected) selectedItems.current[record.id] = record;
              else delete selectedItems.current[record.id];
            });
            const changedIds = new Set(records.map((record) => record.id));
            setSelectedIds((previous) => selected ? Array.from(new Set([...previous, ...changedIds])) : previous.filter((id) => !changedIds.has(id)));
          },
          onSelectAll: (selected: boolean) => setCurrentPageSelected(selected),
        }}
        pagination={{
          current: response.page + 1, pageSize: response.size, total: response.totalElements, showSizeChanger: true,
          pageSizeOptions: [5, 10, 20, 50, 100], showTotal: (total: number) => `전체: ${total.toLocaleString()}대`,
          onChange: (nextPage: number, nextSize?: number) => { if (nextSize && nextSize !== size) { setSize(nextSize); setPage(0); } else setPage(nextPage - 1); },
        }}
        onChange={(_pagination: any, _tableFilters: any, sorter: any) => {
          const sortByField: Record<string, VehicleListV2Sort> = { battery: "BATTERY", allocatedAt: "ALLOCATED_AT", dataUpdatedAt: "IOT_UPDATED_AT", lastWorkAt: "LAST_WORK_AT", lastRideAt: "LAST_RIDE_AT" };
          const nextSort = sorter?.field && sorter?.order ? sortByField[sorter.field] || "ID" : "ID";
          const nextDirection = sorter?.order ? vehicleListV2SortDirection(nextSort, sorter.order) : "ASC";
          setSort(nextSort);
          setDirection(nextDirection);
        }}
        title={() => (
          <Wrapper>
            <TableTitleWrapper>
              <TableTitleTextWrapper>
                <p className="table-title">{R.text.e_scooters_e_bikes}</p>
                <p className="on-offline"><span style={{ display: "inline-flex", alignItems: "center", minWidth: 200 }}>{countsLoading ? <Space size={4}><Spin size="small" />현황 집계 중</Space> : `${R.text.online}: ${globalCounts?.onlineCount ?? "-"} ${R.text.offline}: ${globalCounts?.offlineCount ?? "-"}`}</span></p>
              </TableTitleTextWrapper>
              {!multipleActionsDisabled && (
                <VehicleListV2Actions
                  selection={selectionReady ? selection : null} selectedCount={selectionReady ? selectedCount : 0} taskDefinitions={taskDefinitions}
                  bandNames={bandNames} belongingNames={belongingNames}
                  canControl={hasPermission("ControlScoot")} canStockOut={hasPermission("VehicleStockOut")} canDynamicPrice={hasPermission("DynamicPricingManualSetup")}
                  deployedCount={globalCounts?.deployedCount} canAddTask={hasPermission("AddTaskDefinitionTypeEnable")}
                  showInspect={auth?.band?.id !== RESTRICTED_FILTER_BAND_ID}
                  onLoadLastRide={() => loadLastRides()} lastRideLoading={lastRideWaitingForPage || lastRideLoadingKey === lastRidePageKey}
                  lastRideAtByVehicleId={lastRideAtByVehicleId}
                  selectedItems={selectedItems.current}
                  onSelectedItemsLoaded={(items) => {
                    const ids = new Set(selectedIdsRef.current);
                    items.forEach((item) => {
                      if (ids.has(item.id) && !selectedItems.current[item.id]) selectedItems.current[item.id] = item;
                    });
                  }}
                  onAddTask={() => setAddTaskVisible(true)} onInspect={() => { setInspectMounted(true); setInspectVisible(true); }} onChanged={refresh}
                />
              )}
            </TableTitleWrapper>
            <VehicleListV2Filter
              key={bandId}
              value={filters} qr={qr} imei={imei} showAdminFilters={showAdminFilters} isMainBand={isMainBand} currentBandId={auth?.band?.id}
              bands={bands} belongings={belongings} areas={areas} taskDefinitions={taskDefinitions}
              areaLoading={areasLoading} onAreaOpen={loadAreas}
              onChange={changeFilters}
              onQrChange={setQr} onImeiChange={setImei}
              onReset={() => { changeFilters(initialFilters(isMainBand)); setQr(""); setImei(""); updateDebouncedIdentifiers("", ""); }}
              onSearchNow={() => updateDebouncedIdentifiers(qr, imei)}
            />
          </Wrapper>
        )}
      />
      {inspectMounted && <ScootInspectModal isModalVisible={inspectVisible} setIsModalVisible={() => setInspectVisible(false)} setForceReload={refresh} />}
      {addTaskVisible && <AddTaskDefinitionTypeModal close={() => setAddTaskVisible(false)} fetch={refresh} modalState={{ visible: addTaskVisible }} />}
    </>
  );
}

const VehicleStyleTable = styled(VehicleTable)`
  @media only screen and (max-width: 576px) {
    .ant-pagination-options { display: inline-block; margin-left: 16px; vertical-align: middle; }
  }
`;
const ErrorAlert = styled(Alert)`margin: 0 40px 16px;`;
const CustomLink = styled(Link)`:visited { color: #aa00ff; }`;
